import { useCallback, useEffect, useState } from 'react';
import { useAsk } from '../components/Ask';
import { DateCell } from '../components/DateCell';
import { api, ApiError } from '../lib/api';
import { formatDate, todayValue } from '../lib/dates';
import type { CompanyRole, RateCardRow } from '../lib/types';
import { ROLE_LABELS } from '../lib/types';

/**
 * What a role costs per hour, and when.
 *
 * `rate_cards` sat in the schema from the first migration with nothing reading it, nothing
 * writing it and no rows in it — so every cost figure the plan could have shown was null.
 * The API to fill it in has existed since the costing work; this is the first screen for it,
 * and without one the cost columns are null on every plan for ever.
 *
 * ADMINISTRATORS ONLY, and the card is not rendered at all otherwise. A rate is the most
 * sensitive number in the Hub — it is what the company charges, per person, by grade — and
 * the rule the rest of the code follows is that money needs `company.manage`.
 *
 * VERSIONED BY DATE, never edited in place. A cost is always worked out against the rate that
 * applied when the work happened, so raising a price must not silently re-rate every finished
 * project — which is exactly what editing a row would do. Saving a new rate for a role closes
 * the previous open-ended one the day before it starts.
 */
export function RatesCard({ companyId }: { companyId: string }) {
  const [rates, setRates] = useState<RateCardRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [role, setRole] = useState<CompanyRole>('user');
  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState<string | null>(null);
  const ask = useAsk();

  const load = useCallback(async () => {
    try {
      setRates(await api.get<RateCardRow[]>(`/companies/${companyId}/rates`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the rates');
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(what: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await what();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  async function add() {
    const hourlyRate = Number(amount);
    if (!Number.isFinite(hourlyRate) || hourlyRate < 0 || !from) return;
    await act(async () => {
      await api.post(`/companies/${companyId}/rates`, { role, hourlyRate, effectiveFrom: from });
      setAmount('');
      setFrom(null);
      setAdding(false);
    });
  }

  const today = todayValue();
  /** The one a cost worked out today would actually use, so the card says which is live. */
  const current = (row: RateCardRow) => row.effectiveFrom <= today && (!row.effectiveTo || row.effectiveTo >= today);

  return (
    <div className="card">
      <div className="card-head">
        <h3>Rates</h3>
        <span className="row" style={{ gap: 10 }}>
          {rates && rates.length === 0 && (
            <span className="dim" style={{ fontSize: 12 }}>
              no rates, so every cost reads as unknown
            </span>
          )}
          {!adding && (
            <button type="button" className="btn btn-quiet btn-sm" onClick={() => setAdding(true)}>
              Set a rate
            </button>
          )}
        </span>
      </div>

      <div className="card-pad">
        {error && <div className="callout mb-2">{error}</div>}

        <p className="dim" style={{ margin: '0 0 8px 0', fontSize: 12 }}>
          Administrators only, here and in the plan. A rate is never edited — set a new one and
          the old is kept, so a finished project keeps the cost it was actually worked at.
        </p>

        {adding && (
          <div className="stack-sm mb-2">
            <div className="row wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
              <label className="field" style={{ marginBottom: 0, minWidth: 160 }}>
                <span className="field-label">Role</span>
                <select value={role} onChange={(event) => setRole(event.target.value as CompanyRole)}>
                  {(['user', 'client'] as CompanyRole[]).map((value) => (
                    <option key={value} value={value}>
                      {ROLE_LABELS[value]}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field" style={{ marginBottom: 0, minWidth: 130 }}>
                <span className="field-label">Per hour (ZAR)</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={amount}
                  placeholder="850"
                  onChange={(event) => setAmount(event.target.value)}
                />
              </label>

              <div className="field" style={{ marginBottom: 0 }}>
                <span className="field-label">From</span>
                <DateCell value={from} editable={!busy} emptyLabel="Pick a date" onChange={setFrom} />
              </div>
            </div>

            <span className="field-hint">
              Hours booked before this date keep whatever rate applied then. If a rate for this
              role is already running, it is closed the day before this one starts.
            </span>

            <div className="row" style={{ gap: 7 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!amount || !from || busy}
                onClick={() => void add()}
              >
                Set it
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => {
                  setAdding(false);
                  setAmount('');
                  setFrom(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!rates ? (
          <span className="dim">Loading…</span>
        ) : rates.length === 0 ? (
          <p className="dim" style={{ margin: 0, fontSize: 13 }}>
            Nothing set. The plan's cost columns stay blank until there is a rate to work them
            out from — blank rather than zero, deliberately, because a made-up rate in a budget
            paper is worse than no figure at all.
          </p>
        ) : (
          <div className="stack-sm">
            {rates.map((row) => (
              <div className="row" key={row.id} style={{ gap: 8, alignItems: 'baseline' }}>
                <span className="badge" style={{ minWidth: 84, justifyContent: 'center' }}>
                  {ROLE_LABELS[row.role] ?? row.role}
                </span>
                <span className="mono" style={{ minWidth: 110 }}>
                  {row.currency} {row.hourlyRate}
                </span>
                <span className="grow dim" style={{ minWidth: 0, fontSize: 13 }}>
                  from {formatDate(row.effectiveFrom)}
                  {row.effectiveTo ? ` to ${formatDate(row.effectiveTo)}` : ''}
                  {/*
                    Which one a cost worked out today would use. Several cards per role is the
                    normal state of this table, so "which is the live one" has to be said
                    rather than left to be worked out from two dates.
                  */}
                  {current(row) ? '' : ' · not in effect today'}
                </span>
                {current(row) && <span className="badge badge-ok">in effect</span>}
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  disabled={busy}
                  onClick={async () => {
                    const sure = await ask.confirm({
                      title: `Remove the ${ROLE_LABELS[row.role] ?? row.role} rate of ${row.currency} ${row.hourlyRate}?`,
                      body: 'For a figure typed in wrongly. Every cost in the Hub is worked out from these rather than stored, so removing it changes what the plan says — which is the point of removing a wrong one.\n\nTo record a price change, set a new rate instead: that keeps this one for the work already done at it.',
                      confirmLabel: 'Remove the rate',
                      danger: true,
                    });
                    if (sure) await act(() => api.del(`/companies/${companyId}/rates/${row.id}`));
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
